import { useRef } from 'react';
import { getAISuggestions, getAIReview, getLawyerReview, getGhostTextSuggestion, getAIDraft } from '../services/aiService';
import { useAppContext } from '../contexts/AppContext';

export const useAppHandlers = () => {
  const { state, actions } = useAppContext();
  const { reportText } = state;
  const {
    setReportText,
    setLoading,
    setSupervisorLoading,
    setLawyerLoading,
    setSuggestions,
    setSupervisorReview,
    setLawyerReview,
    setDrafting: setIsDrafting
  } = actions;

  const ghostTextTimeoutRef = useRef(null);

  // Generate AI Analysis
  const generateAnalysis = async () => {
    if (!reportText.trim()) {
      setSuggestions('Please enter some text to analyze.');
      return;
    }

    setLoading(true);
    try {
      const suggestions = await getAISuggestions(reportText);
      setSuggestions(suggestions.text || 'No suggestions available.');
    } catch (error) {
      console.error('Error getting suggestions:', error);
      setSuggestions('Error getting suggestions. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Generate Supervisor Review
  const generateSupervisorReview = async () => {
    console.log('generateSupervisorReview called with reportText:', reportText);
    if (!reportText.trim()) {
      setSupervisorReview('Please enter some text to review or use copy to editor button to add the text to the editor.');
      return;
    }

    setSupervisorLoading(true);
    try {
      console.log('Calling getAIReview...');
      const review = await getAIReview(reportText);
      console.log('Received review:', review);
      setSupervisorReview(review.html || review.text || 'No review available.');
    } catch (error) {
      console.error('Error getting supervisor review:', error);
      setSupervisorReview('Error getting review. Please try again.');
    } finally {
      setSupervisorLoading(false);
    }
  };

  // Generate Lawyer Review
  const generateLawyerReview = async () => {
    if (!reportText.trim()) {
      setLawyerReview('Please enter some text to review or use copy to editor button to add the text to the editor.');
      return;
    }

    setLawyerLoading(true);
    try {
      const review = await getLawyerReview(reportText);
      setLawyerReview(review.html || review.text || 'No legal review available.');
    } catch (error) {
      console.error('Error getting lawyer review:', error);
      setLawyerReview('Error getting legal review. Please try again.');
    } finally {
      setLawyerLoading(false);
    }
  };

  // Handle Generate Draft
  const handleGenerateDraft = async (formData) => {
    setIsDrafting(true);
    setReportText('Generating draft...'); // Provide immediate feedback
    try {
      // Format the structured data into a readable format for the AI
      const formattedData = {
        suspects: formData.suspects,
        victims: formData.victims,
        location: formData.location,
        date: formData.date,
        offense: formData.offense,
        summary: formData.summary
      };
      
      const draft = await getAIDraft(formattedData);
      setReportText(draft);
    } catch (error) {
      console.error('Error generating draft:', error);
      setReportText('Failed to generate draft. Please try again.');
    } finally {
      setIsDrafting(false);
    }
  };

  // Handle Textarea Change with Ghost Text
  const handleTextareaChange = async (e) => {
    const newText = e.target.value;
    setReportText(newText);

    // Clear existing timeout
    if (ghostTextTimeoutRef.current) {
      clearTimeout(ghostTextTimeoutRef.current);
    }

    // Get ghost text suggestion after a delay
    const getGhostSuggestion = async () => {
      if (newText.length > 10 && newText.endsWith(' ')) {
        try {
          await getGhostTextSuggestion(newText);
          // Only show ghost text if the content hasn't changed
          if (newText === reportText) {
            // setGhostText(suggestion); // You'll need to pass setGhostText if needed
          }
        } catch (error) {
          console.error('Error getting ghost text:', error);
        }
      }
    };

    // Set timeout for ghost text
    ghostTextTimeoutRef.current = setTimeout(getGhostSuggestion, 500);
  };

  return {
    generateAnalysis,
    generateSupervisorReview,
    generateLawyerReview,
    handleGenerateDraft,
    handleTextareaChange,
    ghostTextTimeoutRef
  };
}; 